import Cookies from "js-cookie";
import md5 from "md5";
import sha1 from "sha1";
import store from "@utils/redux/store";
import { captureException } from "@utils/sentry";
import { trackGenericEvent } from "@utils/api/tracker";
import {
  shouldAdjustELTV,
  adjustELTV,
  extractAdName,
} from "@utils/eltvAdjustment";
import { routeConstants } from "@utils/constants";
import TestPixel from "./TestPixel.js";

const PIXEL_RETRY_DELAY_MS = 5000;
const PIXEL_RETRY_MAX_ATTEMPTS = 10;

// NOTE(nick): Due to GRX-327, we now have two FB pixels, one that uses erev9
// and one that uses eltv13 as the value for the purchase event.
const FB_EREV9_PIXEL = "1204952429523059";
const FB_ELTV13_PIXEL = "1557275494440599";
const FB_KR_SANDBOX_PIXEL = "2542854726029439";
const FB_HM_PIXEL = "373833553789964";
const DEFAULT_EREV_9 = 85;
const DEFAULT_ELTV_13 = 60;

const FB_KR_PIXELS = [FB_KR_SANDBOX_PIXEL];
const FB_HW_PIXELS = [FB_EREV9_PIXEL, FB_ELTV13_PIXEL];
const FB_HM_PIXELS = [FB_HM_PIXEL];

const KAKAO_PIXEL = "7044192314154893328";
/**
 * A helper class for sending conversion events to Facebook/Pinterest/GA.
 */
export default class ConversionTracker {
  constructor(fbqDisabled = false) {
    this.fbqDisabled = fbqDisabled;
    if (!fbqDisabled) {
      ConversionTracker.initFacebookPixels();
      this.trackFacebookConversion(FB_HW_PIXELS, "PageView", null);
      this.trackFacebookConversion(FB_HM_PIXELS, "PageView", null);
      this.trackFacebookConversion(FB_KR_PIXELS, "PageView", null);
    }

    this.testPixel = new TestPixel();

    // Parse the full URL and save it as part of the tracker instance for later use.
    const params = new URL(document.location).searchParams;
    const urlParams = {};
    params.forEach((value, key) => {
      urlParams[key] = value;
    });
    this.urlParams = urlParams;
  }

  /**
   * Formats a new params object for use in the conv_param1 prop of the Pinterest Pixel tag.
   * NOTE(Rose): Make sure to handle surveyAnswers or its props being undefined due to
   * inconsistencies between US/nonUS survey data (e.g. ageRange is US, age is nonUS).
   */
  static getPinParams(plan, surveyAnswers) {
    const params = {
      planId: plan.braintree_id,
      duration: plan.duration,
    };
    const requestedParams = [
      "ageRange",
      "age",
      "weight",
      "idealWeight",
      "weightKg",
    ];

    if (surveyAnswers) {
      requestedParams.forEach((key) => {
        if (surveyAnswers[key]) {
          params[key] = surveyAnswers[key];
        }
      });

      if (params.weight && params.idealWeight) {
        params.weightLossGoal = params.weight - params.idealWeight;
      } else if (params.weightKg && surveyAnswers.weightLossGoal) {
        params.weightLossGoalKg = surveyAnswers.weightLossGoal;
        params.idealWeightKg = params.weightKg - surveyAnswers.weightLossGoal;
      }
    }
    return params;
  }

  static convertToPinConvParam(params) {
    const convParam = [];
    Object.keys(params).forEach((key) => {
      const param = params[key];
      if (param != null && param) {
        convParam.push(`${key}:${param.toString()}`);
      }
    });
    return convParam.join(",");
  }

  /**
   * Helper function to retry firing a pixel if the pixel hasn't loaded yet.
   *
   * NOTE(patrick): If a user has an adblocker that prevents a pixel from loading,
   *                the global variable won't ever load and we'll hit the attempt
   *                limit.
   *
   * @param {string} fnName The name of the function bound to the window object to fire.
   * @param {array} args An array of arguments to pass into fn. It will be spread
   *                     into the params.
   * @param {number} attempt The attempt number, used to track number of retries.
   */
  static firePixelWithRetry(fnName, args = [], attempt = 0) {
    if (window[fnName] && typeof window[fnName] === "function") {
      window[fnName](...args);
    } else if (attempt <= PIXEL_RETRY_MAX_ATTEMPTS) {
      setTimeout(() => {
        ConversionTracker.firePixelWithRetry(fnName, args, attempt + 1);
      }, PIXEL_RETRY_DELAY_MS);
      console.info(
        `window.${fnName} doesn't exist, waiting ${Math.floor(
          PIXEL_RETRY_DELAY_MS / 1000
        )}s before retrying.`
      );
    } else {
      console.error(`window.${fnName} doesn't exist. Max retries reached.`);
    }
  }

  /**
   * Initialize Facebook pixel. Make sure to add the LDU flag if CCPA
   * opt out cookie is set.
   * @param {*} fbParams
   */
  static initFacebookPixels(fbParams = null) {
    // Check to add LDU flag for CCPA opt out.
    const ccpaOptOut = Cookies.get("ccpaOptOut");
    if (ccpaOptOut) {
      window.fbq("dataProcessingOptions", ["LDU"], 1, 1000);
    }

    const initPixel = (pixel) => {
      ConversionTracker.firePixelWithRetry("fbq", ["init", pixel, fbParams]);
    };

    initPixel(FB_EREV9_PIXEL);
    initPixel(FB_ELTV13_PIXEL);
    initPixel(FB_KR_SANDBOX_PIXEL);
    initPixel(FB_HM_PIXEL);
  }

  /**
   * Initializes all advanced matching pixels
   */
  initAdvancedMatching(email, name) {
    this.initFBWithAdvancedMatching(email, name);
    ConversionTracker.initPinterestWithAdvancedMatching(email);
  }

  /**
   * Enables the advance matching feature for Facebook Pixel.
   */
  initFBWithAdvancedMatching(email, name) {
    try {
      const fbParams = {};
      if (email && email.length > 0 && name && name.length > 0) {
        fbParams.em = email.toLowerCase();

        const nameParts = name.trim().split(" ");
        fbParams.fn = nameParts[0];
        if (nameParts.length > 1) {
          fbParams.ln = nameParts[nameParts.length - 1];
        }
      }
      const { userData } = store.getState();
      if (userData?.user_id) {
        fbParams.external_id = userData.user_id;
      }

      ConversionTracker.initFacebookPixels(fbParams);
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Enables advanced matching for Pinterest
   */
  static initPinterestWithAdvancedMatching(email) {
    try {
      if (email && email.length > 0) {
        ConversionTracker.firePixelWithRetry("pintrk", [
          "load",
          "2620804621622",
          {
            em: email.toLowerCase(),
          },
        ]);
      }
    } catch (e) {
      captureException(e);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  trackLandingPageViewHM() {
    ConversionTracker.trackGoogleConversion(
      "AW-783925782/v0hcCNvXmOwBEJaE5_UC"
    );
  }

  trackLandingPageViewKR() {
    ConversionTracker.trackGoogleAdwordsConversion(
      "AW-867388860/NUZMCMqBpLUBELybzZ0D"
    );

    ConversionTracker.trackKakaoConversion(KAKAO_PIXEL);
  }

  /**
   * Tracks the 'Lead' event.
   */
  trackLead(email, name, surveyAnswers) {
    try {
      this.initAdvancedMatching(email, name);

      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {};
      if (surveyAnswers && surveyAnswers.weightLossGoal) {
        fbParams.value = surveyAnswers.weightLossGoal;
      }
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_HW_PIXELS, "Lead", fbParams);

      ConversionTracker.firePixelWithRetry("pintrk", ["track", "lead"]);

      // Bing.
      window.uetq = window.uetq || [];
      window.uetq.push({
        ec: "Lead",
        ea: "Lead",
        el: "Lead",
        ev: "0",
      });

      ConversionTracker.trackGoogleConversion(
        "AW-999837065/eTPICKjA3W4QiZvh3AM"
      );

      ConversionTracker.trackGoogleAdwordsConversion(
        "AW-783925782/tDBSCM2Kq4sBEJaE5_UC"
      );

      ConversionTracker.trackArtsaiConversion(
        "DC-10175718/conte0/email0+standard"
      );

      // Twitter JP.
      ConversionTracker.trackTwitterConversion("nz3fb");

      // Impact initial identify call.
      ConversionTracker.trackImpactLead(email);

      // LiveIntent.
      ConversionTracker.trackLiveIntentLead(email);

      // Fire a Google Tag Manager event and add data to the data layer.
      window.dataLayer.push({
        event: "lead",
        email,
        name,
        program: "healthy-weight",
        ...surveyAnswers,
      });

      // Fire test pixel
      this.testPixel.trackEvent("lead", {
        email,
      });
    } catch (e) {
      captureException(e);
    }
    ConversionTracker.trackPodsightsLead("healthy-weight");
  }

  /**
   * Tracks the 'Lead' event for Healthy Mind users.
   */
  trackLeadHM(email, name, surveyAnswers) {
    try {
      this.initAdvancedMatching(email, name);

      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {};
      fbParams.product = "hm";

      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_HW_PIXELS, "Lead", fbParams);
      this.trackFacebookConversion(FB_HM_PIXELS, "Lead", fbParams);

      ConversionTracker.trackGoogleConversion(
        "AW-783925782/RxMYCOD3uuwBEJaE5_UC"
      );

      window.dataLayer.push({
        event: "lead",
        email,
        name,
        program: "healthy-mind",
        ...surveyAnswers,
      });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Lead' event for Healthy Mind users.
   */
  trackLeadKR(email, name, surveyAnswers) {
    try {
      this.initAdvancedMatching(email, name);

      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {};
      fbParams.product = "hm";
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_KR_PIXELS, "Lead", fbParams);

      ConversionTracker.trackGoogleAdwordsConversion(
        "AW-867388860/Hb_FCK60nbUBELybzZ0D"
      );

      ConversionTracker.trackKakaoConversion(KAKAO_PIXEL, "lead");

      window.dataLayer.push({
        event: "lead",
        email,
        name,
        program: "healthy-mind",
        ...surveyAnswers,
      });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Initiate Checkout' event.
   */
  trackInitiateCheckout(plan, surveyAnswers) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {
        value: plan.lv,
        currency: plan.currency ? plan.currency : "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
      };
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_HW_PIXELS, "InitiateCheckout", fbParams);
      ConversionTracker.firePixelWithRetry("pintrk", ["track", "addtocart"]);

      // Bing
      window.uetq = window.uetq || [];
      window.uetq.push({
        ec: "Initiate Checkout",
        ea: "Initiate Checkout",
        el: "Initiate Checkout",
        ev: "0",
      });

      ConversionTracker.trackGoogleConversion(
        "AW-999837065/IW5rCPvf6G4QiZvh3AM"
      );

      ConversionTracker.trackGoogleAdwordsConversion(
        "AW-783925782/OeaPCP-QuYsBEJaE5_UC"
      );

      ConversionTracker.trackArtsaiConversion(
        "DC-10175718/purch0/initi0+standard"
      );

      // Twitter JP.
      ConversionTracker.trackTwitterConversion("nz3fc");

      const { geoLocation } = store.getState();
      // UK TV - All Response Media - Tag4ARM
      if (["UK", "GB", "IE"].includes(geoLocation.country_code)) {
        ConversionTracker.trackUKTVSurveyResponse(surveyAnswers);
      }

      window.dataLayer.push({
        event: "initiate-checkout",
        program: "healthy-weight",
        plan,
        ...surveyAnswers,
      });

      // Fire test pixel
      this.testPixel.trackEvent("checkout", {
        lv: plan.lv,
        braintree_id: plan.braintree_id,
        currency: plan.currency,
      });
    } catch (e) {
      captureException(e);
    }
    ConversionTracker.trackPodsightsCheckout(plan);
  }

  /**
   * Tracks the 'Initiate Checkout' event for Healthy Mind users.
   */
  trackInitiateCheckoutHM(plan, surveyAnswers) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {
        value: plan.lv,
        currency: plan.currency ? plan.currency : "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        product: "hm",
      };

      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_HW_PIXELS, "InitiateCheckout", fbParams);
      this.trackFacebookConversion(FB_HM_PIXELS, "InitiateCheckout", fbParams);
      ConversionTracker.firePixelWithRetry("pintrk", ["track", "addtocart"]);

      window.dataLayer.push({
        event: "initiate-checkout",
        program: "healthy-mind",
        plan,
        ...surveyAnswers,
      });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Initiate Checkout' event for Korean users.
   */
  trackInitiateCheckoutKR(plan, surveyAnswers) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const value = ConversionTracker.getELTVByPlanKR(plan);
      const fbParams = {
        value,
        currency: plan.currency ? plan.currency : "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        product: plan.curriculum,
      };

      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_KR_PIXELS, "InitiateCheckout", fbParams);
      ConversionTracker.firePixelWithRetry("pintrk", ["track", "addtocart"]);

      ConversionTracker.trackGoogleAdwordsConversion(
        "AW-867388860/GdHgCKbnv3AQvJvNnQM"
      );

      ConversionTracker.trackKakaoConversion(KAKAO_PIXEL, "initiateCheckout", {
        ...plan,
        value,
      });

      window.dataLayer.push({
        event: "initiate-checkout",
        program: "healthy-mind",
        plan,
        ...surveyAnswers,
      });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Purchase' event.
   */
  trackPurchase(email, plan, surveyAnswers, order) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {
        value: order.erev_9_months,
        currency: "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
      };
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      // NOTE(di): For FB algorithm optimization, we need to track both Purchase and StartTrial events with identical properties.
      this.trackFacebookConversion(FB_HW_PIXELS, "StartTrial", fbParams);
      const { userData, geoLocation } = store.getState();
      const eltv13Value = shouldAdjustELTV()
        ? adjustELTV(order.eltv_13_months, geoLocation.country_code)
        : order.eltv_13_months;
      if (eltv13Value !== order.eltv_13_months) {
        trackGenericEvent("AdjustedELTV13", {
          originalValue: order.eltv_13_months,
          adjustedValue: eltv13Value,
          adName: extractAdName(),
        });
      }
      if (!this.fbqDisabled) {
        this.trackFacebookConversion([FB_EREV9_PIXEL], "Purchase", fbParams);
        this.trackFacebookConversion(
          [FB_ELTV13_PIXEL],
          "Purchase",
          Object.assign({}, fbParams, { value: eltv13Value })
        );
      }

      const pinParams = ConversionTracker.getPinParams(plan, surveyAnswers);
      const convParam = ConversionTracker.convertToPinConvParam(pinParams);

      ConversionTracker.trackHealthlineConversion(
        this.urlParams.clid,
        this.urlParams.utm_campaign
      );

      ConversionTracker.firePixelWithRetry("pintrk", [
        "track",
        "checkout",
        {
          value: order.erev_9_months,
          order_quantity: 1,
          currency: "USD",
          order_id: order.subscriptionId,
          conv_param1: convParam,
        },
      ]);

      // Bing.
      window.uetq = window.uetq || [];
      window.uetq.push({
        ec: "Purchase",
        ea: "Purchase",
        el: "Purchase",
        ev: "0",
        gv: order.erev_9_months.toFixed(3),
        gc: "USD",
      });

      // Yahoo/Gemini
      window.dotq = window.dotq || [];
      window.dotq.push({
        projectId: "10000",
        properties: {
          pixelId: "10074549",
          qstrings: {
            ec: "signup",
            et: "custom",
          },
        },
      });

      ConversionTracker.trackGoogleAdwordsConversion(
        "AW-783925782/XdY_CJWPuYsBEJaE5_UC",
        order.erev_9_months
      );

      ConversionTracker.trackArtsaiConversion(
        "DC-10175718/purch0/purch0+standard",
        { u1: order.subscriptionId, u2: order.erev_9_months }
      );

      // Twitter JP.
      ConversionTracker.trackTwitterConversion("nz3f8");

      // Impact.
      ConversionTracker.trackImpactConversion(plan, order);

      // LiveIntent.
      ConversionTracker.trackLiveIntentConversion(plan, order);

      // UK TV - All Response Media - Tag4ARM
      if (["UK", "GB", "IE"].includes(geoLocation.country_code)) {
        ConversionTracker.trackUKTVConversion(
          order.erev_9_months,
          userData.user_id
        );
      }

      window.dataLayer.push({
        event: "purchase",
        program: "healthy-weight",
        email,
        plan,
        ...surveyAnswers,
        order,
      });

      // Fire test pixel
      this.testPixel.trackEvent("purchase", {
        erev_9_months: order.erev_9_months,
        currency: "USD",
        braintree_id: plan.braintree_id,
        order_id: order.subscriptionId,
        user_id: userData.user_id,
      });
    } catch (e) {
      captureException(e);
    }
    ConversionTracker.trackPodsightsPurchase(order);
  }

  /**
   * Tracks the 'Purchase' event for Healthy Mind users.
   */
  trackPurchaseHM(email, plan, surveyAnswers, order, isSoldOut = false) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const valueErev9 = !isSoldOut ? order.erev_9_months : DEFAULT_EREV_9;
      const valueEltv13 = !isSoldOut ? order.eltv_13_months : DEFAULT_ELTV_13;
      const { deviceInformation } = store.getState();

      const fbParams = {
        value: valueErev9,
        currency: "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        product: "hm",
        device_os: deviceInformation?.device_os,
      };
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);

      // NOTE(di): For FB algorithm optimization, we need to track both Purchase and StartTrial events with identical properties.
      this.trackFacebookConversion(FB_HW_PIXELS, "StartTrial", fbParams);
      this.trackFacebookConversion(FB_HM_PIXELS, "StartTrial", fbParams);
      if (!this.fbqDisabled) {
        this.trackFacebookConversion([FB_EREV9_PIXEL], "Purchase", fbParams);
        this.trackFacebookConversion(
          [FB_ELTV13_PIXEL],
          "Purchase",
          Object.assign({}, fbParams, { value: valueEltv13 })
        );
        this.trackFacebookConversion(FB_HM_PIXELS, "Purchase", fbParams);
      }

      ConversionTracker.trackGoogleAdwordsConversion(
        "AW-783925782/fCpkCLbkqOwBEJaE5_UC",
        valueErev9
      );

      window.dataLayer.push({
        event: "purchase",
        program: "healthy-mind",
        email,
        plan,
        ...surveyAnswers,
        order,
        isSoldOut,
      });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Purchase' event for Healthy Mind users.
   */
  trackPurchaseKR(email, plan, surveyAnswers, order, isSoldOut = false) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const value = ConversionTracker.getELTVByPlanKR(plan);
      const valueEltv13 = !isSoldOut ? order.eltv_13_months : DEFAULT_ELTV_13;
      const { deviceInformation } = store.getState();
      const fbParams = {
        value,
        currency: plan.currency || "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        product: plan.curriculum,
        device_os: deviceInformation?.device_os,
      };
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);

      // NOTE(di): For FB algorithm optimization, we need to track both Purchase and StartTrial events with identical properties.
      this.trackFacebookConversion(FB_KR_PIXELS, "StartTrial", fbParams);
      if (!this.fbqDisabled) {
        this.trackFacebookConversion(FB_KR_PIXELS, "Purchase", fbParams);
      }
      ConversionTracker.trackKakaoConversion(KAKAO_PIXEL, "purchase", {
        ...plan,
        value,
      });
      ConversionTracker.trackGoogleAdwordsConversion(
        "AW-867388860/u_2oCLqv-WwQvJvNnQM",
        fbParams.value,
        fbParams.currency
      );
      window.dataLayer.push({
        event: "purchase",
        program: "dietary",
        email,
        plan,
        ...surveyAnswers,
        order,
        isSoldOut,
      });
    } catch (e) {
      captureException(e);
    }
  }

  trackViewContent() {
    this.trackViewContentWithParams({ value: "2.00", currency: "USD" });
  }

  trackViewContentWithParams(params) {
    this.trackFacebookConversion(FB_HW_PIXELS, "ViewContent", params);
    window.dataLayer.push({
      event: "view",
      params,
      url: window.location.href,
      urlPathname: window.location.pathname,
      urlHash: window.location.hash,
      urlSearchParams: window.location.search,
    });
  }

  trackViewContentWithParamsExsf0(params) {
    const { serverContext, language } = store.getState();
    if (serverContext.is_paid_traffic && language === "en") {
      this.trackViewContentWithParams(params);
    }
  }

  static getCustomParams(surveyAnswers) {
    // NOTE(di): We would like to parse and send all survey answers to Facebook.
    const params = {};

    // NOTE: Facebook has flagged that we shouldn’t be passing the following parameters from the buyflow.
    const notAllowedParamList = [
      "gender",
      "antibiotic",
      "weight",
      "weightKg",
      "pregnant",
      "email",
      "currentHealthRisk",
      "lifeEvent",
      "lifeEventMale",
      "currentHealthRiskMale",
    ];

    if (surveyAnswers) {
      Object.entries(surveyAnswers).forEach(([key, value]) => {
        if (notAllowedParamList.includes(key)) return;
        if (Array.isArray(value)) {
          params[key] = value.join(",");
        } else {
          params[key] = value;
        }
      });
    }

    // NOTE(di): Since Facebook doesn't allow advance calculation on passed values, we pre-calculate some key values here.
    if (params.weight && params.idealWeight) {
      params.weightLossGoal = params.weight - params.idealWeight;
    } else if (params.weightKg && params.idealWeightKg) {
      const weightLossGoalKg = params.weightKg - params.idealWeightKg;
      params.weightLossGoal = Math.round(weightLossGoalKg * 2.2046);
    } else if (params.weightLossGoalKg) {
      params.weightLossGoal = Math.round(params.weightLossGoalKg * 2.2046);
    }

    return params;
  }

  trackFacebookConversion(pixels, eventName, params) {
    if (this.fbqDisabled) {
      return;
    }

    pixels.forEach((pixel) => {
      ConversionTracker.firePixelWithRetry("fbq", [
        "trackSingle",
        pixel,
        eventName,
        params,
      ]);
    });
  }

  static trackTwitterConversion(pid, attempt = 0) {
    if (
      window.twttr &&
      window.twttr.conversion &&
      window.twttr.conversion.trackPid
    ) {
      window.twttr.conversion.trackPid(pid, {
        tw_sale_amount: 0,
        tw_order_quantity: 0,
      });
    } else if (attempt <= PIXEL_RETRY_MAX_ATTEMPTS) {
      setTimeout(() => {
        ConversionTracker.trackTwitterConversion(pid, attempt + 1);
      }, PIXEL_RETRY_DELAY_MS);
    }
  }

  static trackHealthlineConversion(clid, utmCampaign) {
    // For the healthline pixel the revenue is hardcoded.
    const { geoLocation } = store.getState();
    let revenue = geoLocation.country_code === "US" ? 40 : 30;

    if (utmCampaign === "cpm") {
      revenue = 0;
    }

    const healthLineURL = new URL(
      "https://activation.healthline.com/api/member-offers/postback"
    );
    healthLineURL.searchParams.append("clid", clid || -1);
    healthLineURL.searchParams.append("rev", revenue);

    fetch(healthLineURL)
      .then((response) => {
        if (response.ok) {
          trackGenericEvent("HealthlineSignup");
        } else {
          trackGenericEvent("HealthlineFail");
        }
      })
      .catch(() => {
        trackGenericEvent("HealthlineFail");
      });
  }

  static trackGoogleConversion(sendTo) {
    ConversionTracker.firePixelWithRetry("gtag", [
      "event",
      "conversion",
      { send_to: sendTo },
    ]);
  }

  static trackArtsaiConversion(sendTo, additionalParams = {}) {
    ConversionTracker.firePixelWithRetry("gtag", [
      "event",
      "conversion",
      { allow_custom_scripts: true, send_to: sendTo, ...additionalParams },
    ]);
  }

  // This is a tag manager pixel that's tied to google adwords.
  static trackGoogleAdwordsConversion(sendTo, value = 1.0, currency = "USD") {
    ConversionTracker.firePixelWithRetry("gtag", [
      "event",
      "conversion",
      {
        send_to: sendTo,
        value,
        currency,
      },
    ]);
  }

  /**
   * Impact identify call.
   */
  static trackImpactLead(email) {
    const hashedEmail = sha1(email) || "";
    const { serverContext, userData } = store.getState();
    const id = serverContext?.user_id || userData?.mixpanel_distinct_id || "";
    ConversionTracker.firePixelWithRetry("ire", [
      "identify",
      { customerId: id, customerEmail: hashedEmail },
    ]);
  }

  /**
   * Impact conversion call.
   */
  static trackImpactConversion(plan, order) {
    const { totalAmount, discounted_plan_price } = plan;
    const { routeId, userData } = store.getState();

    let subTotal = 0;

    if (totalAmount != null && totalAmount && totalAmount.length > 1) {
      subTotal = parseInt(totalAmount.slice(1), 10);
    }

    if (
      routeId === routeConstants.affiliateNoTrial ||
      routeId === routeConstants.affiliateNoTrialPromo
    ) {
      subTotal = discounted_plan_price;
    }

    let text1 = "Trial";
    let category = "Trial";

    if (!plan.has_trial) {
      text1 = "Sale";
      category = "Subscribed";
    }

    const payload = {
      orderId: order.subscriptionId,
      customerId: userData.user_id,
      customerStatus: "New",
      customerEmail: sha1(order.email),
      currencyCode: plan.currency,
      text1,
      orderPromoCode: plan.promoCodeApplied,
      items: [
        {
          subTotal,
          category,
          sku: plan.braintree_id,
          quantity: 1,
        },
      ],
    };

    ConversionTracker.firePixelWithRetry("ire", [
      "trackConversion",
      15420,
      payload,
      {
        verifySiteDefinitionMatch: true,
      },
    ]);
  }

  /**
   * LiveIntent email_signup event.
   * @param {*} email
   */
  static trackLiveIntentLead(email) {
    window.liQ = window.liQ || [];
    window.liQ.push({
      event: "conversion",
      name: "email_signup",
      emailHash: md5(email),
    });
  }

  /**
   * LiveIntent product_purchase event.
   * @param {*} plan
   * @param {*} order
   */
  static trackLiveIntentConversion(plan, order) {
    const { totalAmount } = plan;
    let price = 0;
    if (totalAmount != null && totalAmount && totalAmount.length > 1) {
      price = parseInt(totalAmount.slice(1), 10);
    }
    window.liQ = window.liQ || [];
    window.liQ.push({
      event: "conversion",
      name: "product_purchase",
      emailHash: md5(order.email),
      conversionId: order.subscriptionId,
      amount: price,
      currency: plan.currency,
      items: [
        {
          id: plan.braintree_id,
          price,
          quantity: 1,
          currency: plan.currency,
        },
      ],
    });
  }

  static trackUKTVSurveyResponse(surveyAnswers) {
    window.fpdTrackSurveyResponse(
      surveyAnswers
        ? JSON.stringify({
            whereDidYouHear: surveyAnswers.whereDidYouHear,
            whereDidYouHearAdditional: surveyAnswers.hdyhauAdditionalResponse,
          })
        : "{}"
    );
  }

  static trackUKTVConversion(eRev9, userId) {
    window.fpdTrackPurchase(eRev9, userId);
  }

  static trackKakaoConversion(pid, type, params, attempt = 0) {
    try {
      if (window.kakaoPixel) {
        window.kakaoPixel(pid).pageView(); // type === "visit"
        switch (type) {
          case "initiateCheckout":
            window.kakaoPixel(pid).signUp("SignUp");
            break;
          case "lead":
            window.kakaoPixel(pid).participation("Consulting");
            break;
          case "purchase":
            window.kakaoPixel(pid).purchase({
              total_quantity: "1", // 주문 내 상품 개수(optional)
              total_price: { value: params.value }, // 주문 총 가격(optional)
              currency: params.currency || "KRW", // 주문 가격의 화폐 단위(optional, 기본 값은 KRW)
              products: [
                // 주문 내 상품 정보(optional)
                {
                  name: params.curriculum,
                  content_ids: params.braintree_id,
                  product_type: "product",
                },
              ],
            });
            break;
          default:
            break;
        }

        window.twttr.conversion.trackPid(pid, {
          tw_sale_amount: 0,
          tw_order_quantity: 0,
        });
      } else if (attempt <= PIXEL_RETRY_MAX_ATTEMPTS) {
        setTimeout(() => {
          ConversionTracker.trackKakaoConversion(pid, attempt + 1);
        }, PIXEL_RETRY_DELAY_MS);
      }
    } catch (e) {
      return null;
    }
  }

  static getELTVByPlanKR(plan) {
    const { currency = "KRW", billing_cycle_in_months = 2 } = plan;
    const id = `${currency}_${billing_cycle_in_months}`;
    let eLTV = currency === "KRW" ? 20000 : 20;
    if (id === "KRW_1") {
      eLTV = 10000;
    }
    if (id === "KRW_2") {
      eLTV = 20000;
    }
    if (id === "KRW_4") {
      eLTV = 30000;
    }
    if (id === "KRW_6") {
      eLTV = 35000;
    }
    if (id === "USD_1") {
      eLTV = 10;
    }
    if (id === "USD_2") {
      eLTV = 20;
    }
    if (id === "USD_4") {
      eLTV = 30;
    }
    if (id === "USD_6") {
      eLTV = 35;
    }
    return eLTV;
  }

  static trackPodsightsLead(program) {
    try {
      ConversionTracker.firePixelWithRetry("pdst", [
        "lead",
        {
          category: program,
        },
      ]);
    } catch (e) {
      captureException(e);
    }
  }

  static trackPodsightsCheckout(plan) {
    try {
      const { checkout } = store.getState();
      ConversionTracker.firePixelWithRetry("pdst", [
        "checkout",
        {
          value: plan.lv,
          currency: plan.currency ? plan.currency : "USD",
          discount_code: checkout.promoCodeApplied,
        },
      ]);
    } catch (e) {
      captureException(e);
    }
  }

  static trackPodsightsPurchase(order) {
    try {
      const { checkout } = store.getState();
      ConversionTracker.firePixelWithRetry("pdst", [
        "purchase",
        {
          value: order.eltv_13_months,
          currency: "USD",
          order_id: order.subscriptionId,
          discount_code: checkout.promoCodeApplied,
        },
      ]);
    } catch (e) {
      captureException(e);
    }
  }
}
